import {
  Autocomplete,
  Stack,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { UtilizacionCapacidadRow } from "../../../api/reports";
import { downloadReportFile } from "../../../components/utils/downloadXlsx";
import { useCentros, type Centro } from "../../../hooks/useCentros";
import { useUtilizacionCapacidadReport } from "../../../hooks/useReportJob";

export default function UtilizacionCapacidadPage() {
  const { t } = useTranslation<"translation">();
  const [desde, setDesde] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [hasta, setHasta] = useState(dayjs().format("YYYY-MM-DD"));
  const [centroSel, setCentroSel] = useState<Centro | null>(null);

  const { rows: centros } = useCentros();

  const { run, useStatus } = useUtilizacionCapacidadReport();
  const [jobId, setJobId] = useState<string>();
  const { data: status } = useStatus(jobId);

  const lanzar = async () => {
    const payload = {
      desde,
      hasta,
      centroId: centroSel?.id,
    };
    const { jobId } = await run(payload);
    setJobId(jobId);
  };

  const handleDownload = async () => {
    if (status?.result?.url) {
      try {
        await downloadReportFile(status.result.url);
      } catch (error) {
        console.error("Error al descargar:", error);
      }
    }
  };

  const rows = status?.result?.rows ?? [];

  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t("utilizacionCapacidad.titulo")}</Typography>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <TextField
          type="date"
          size="small"
          label={t("utilizacionCapacidad.filtros.desde")}
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
        />
        <TextField
          type="date"
          size="small"
          label={t("utilizacionCapacidad.filtros.hasta")}
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
        />
        <Autocomplete
          options={centros.filter((c) => c.nombre !== "OTHER")}
          getOptionLabel={(o) => o.nombre}
          value={centroSel}
          onChange={(_, v) => setCentroSel(v)}
          renderInput={(p) => (
            <TextField {...p} label={t("filters.centro")} size="small" />
          )}
          sx={{ width: 220 }}
        />
        <Button variant="contained" onClick={lanzar}>
          {t("utilizacionCapacidad.filtros.generar")}
        </Button>
      </Stack>

      {status && !status.done && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={24} />
          <Typography>{t("utilizacionCapacidad.estado.generando")}</Typography>
        </Stack>
      )}

      {status?.done && status.result?.url && (
        <Alert severity="success" sx={{ width: "fit-content" }}>
          XLSX:&nbsp;
          <Button
            variant="text"
            size="small"
            onClick={handleDownload}
            sx={{ textTransform: "none", textDecoration: "underline", p: 0, minWidth: "auto" }}
          >
            {t("utilizacionCapacidad.estado.descargar")}
          </Button>
        </Alert>
      )}

      {status?.done && (
        <Paper sx={{ p: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("utilizacionCapacidad.tabla.centro")}</TableCell>
                <TableCell>{t("utilizacionCapacidad.tabla.hora")}</TableCell>
                <TableCell>{t("utilizacionCapacidad.tabla.disponibles")}</TableCell>
                <TableCell>{t("utilizacionCapacidad.tabla.reservadas")}</TableCell>
                <TableCell>{t("utilizacionCapacidad.tabla.pct")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r: UtilizacionCapacidadRow, idx: number) => (
                <TableRow key={`${r.centroId}-${r.hora}-${idx}`}>
                  <TableCell>{r.centro}</TableCell>
                  <TableCell>{String(r.hora).padStart(2, "0")}:00</TableCell>
                  <TableCell>{r.horasDisponibles.toFixed(2)}</TableCell>
                  <TableCell>{r.horasReservadas.toFixed(2)}</TableCell>
                  <TableCell>{r.pct.toFixed(1)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}
